import { api } from "./apiSlice";

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface StockCounts {
  totalStock: number;
  safeUnits: number;
  expiredUnits: number;
  nearingExpiryUnits: number;
}

export interface BloodGroupBucket {
  blood_group: string;
  count: number;
  available_units: number;
}

export interface BloodUnitRow {
  id: string;
  donationId: string;
  inventoryId: string | null;
  status: string;
  volume: number;
  bagType: string;
  expiryDate: string;
  consumed: boolean;
  disposed: boolean;
  donorName?: string | null;
  donorBloodGroup?: string | null;
  donationStartTime?: string | null;
}

export const inventoryApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getStockCounts: builder.mutation<StockCounts, { inventoryId: string }>({
      query: (body) => ({ url: "/blood/stock-counts", method: "POST", body }),
      transformResponse: (res: ApiResponse<StockCounts>) => res.data,
    }),
    getUnitsByBloodGroup: builder.mutation<{ data: BloodGroupBucket[]; totalAvailableUnits: number; totalCount: number }, { inventoryId: string }>({
      query: (body) => ({ url: "/blood/by-blood-group", method: "POST", body }),
      transformResponse: (res: ApiResponse<{ data: BloodGroupBucket[]; totalAvailableUnits: number; totalCount: number }>) => res.data,
    }),
    getUnitsByInventory: builder.mutation<{ units: BloodUnitRow[]; pagination: { page: number; limit: number } }, { inventoryId: string; page?: number; limit?: number }>({
      query: (body) => ({ url: "/blood/by-inventory", method: "POST", body }),
      transformResponse: (res: ApiResponse<{ units: BloodUnitRow[]; pagination: { page: number; limit: number } }>) => res.data,
    }),
    checkAvailabilityByDeadline: builder.mutation<
      { totalAvailableUnits: number; matchingCount: number },
      { medicalEstablishmentId: string; bloodGroup?: string; deadline: string; userId?: string }
    >({
      query: (body) => ({ url: "/blood/check-availability-by-deadline", method: "POST", body }),
      transformResponse: (res: ApiResponse<{ totalAvailableUnits: number; matchingCount: number }>) => res.data,
    }),
    discardUnit: builder.mutation<BloodUnitRow, { bloodId: string }>({
      query: (body) => ({ url: "/blood/discard-unit", method: "POST", body }),
      transformResponse: (res: ApiResponse<BloodUnitRow>) => res.data,
      invalidatesTags: ["Blood", "Inventory"],
    }),
    getInventoryByEstablishment: builder.query<unknown[], string>({
      query: (establishmentId) => `/medical-establishments/${establishmentId}/inventory`,
      transformResponse: (res: ApiResponse<{ inventory: unknown[] }>) => res.data.inventory,
      providesTags: ["Inventory"],
    }),
  }),
});

export const {
  useGetStockCountsMutation,
  useGetUnitsByBloodGroupMutation,
  useGetUnitsByInventoryMutation,
  useCheckAvailabilityByDeadlineMutation,
  useDiscardUnitMutation,
  useGetInventoryByEstablishmentQuery,
} = inventoryApi;
